import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Phone, Mail, Building2, Briefcase, Globe, Upload, Pencil, Lock, Camera, QrCode, Share2, Copy, Check, ChevronUp, ChevronDown } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { fetchProfile, updateProfile, uploadBusinessCard, verifyAdminPin, deleteBusinessCard, reorderCards } from "@/lib/api";

function Field({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-zinc-100 last:border-0">
      <Icon className="w-4 h-4 text-zinc-400 mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-[10px] font-bold text-zinc-400 tracking-widest uppercase">{label}</p>
        <p className="text-sm font-semibold text-zinc-900 mt-0.5">{value}</p>
      </div>
    </div>
  );
}

function EditField({ label, value, onChange, placeholder, type = "text" }) {
  return (
    <div className="mb-3">
      <label className="block text-[10px] font-bold text-zinc-400 tracking-widest uppercase mb-1">{label}</label>
      <input
        type={type}
        value={value || ""}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2.5 text-sm font-body border border-zinc-200 rounded-md bg-white text-zinc-950 focus:ring-2 focus:ring-orange-500 focus:ring-offset-1 outline-none"
      />
    </div>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button onClick={handleCopy} className="p-1.5 rounded-md hover:bg-zinc-200 transition-colors flex-shrink-0" title="Copy link">
      {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-zinc-500" />}
    </button>
  );
}

function saveQR(name) {
  const svg = document.querySelector("#qr-container svg");
  if (!svg) return;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const data = new XMLSerializer().serializeToString(svg);
  const img = new Image();
  img.onload = () => {
    canvas.width = img.width * 2;
    canvas.height = img.height * 2;
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const a = document.createElement("a");
    a.download = `${name}-CHART-Networking-QR.png`;
    a.href = canvas.toDataURL("image/png");
    a.click();
  };
  img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(data)));
}

function shareProfile(name, url) {
  if (navigator.share) {
    navigator.share({ title: `${name} | CHART Networking`, text: `Check out ${name}'s profile on CHART Networking`, url });
  } else {
    navigator.clipboard.writeText(url);
    alert("Link copied to clipboard!");
  }
}

export default function MemberProfile() {
  const { name } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinError, setPinError] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingCard, setUploadingCard] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    loadProfile();
  }, [name]);

  async function loadProfile() {
    try {
      const data = await fetchProfile(decodeURIComponent(name));
      setProfile(data);
      document.title = `${data.name} | CHART Networking`;
      setForm({
        phone: data.phone || "",
        email: data.email || "",
        businessName: data.businessName || "",
        title: data.title || "",
        website: data.website || "",
        businesses: data.businesses || [],
      });
    } catch {
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }

  async function handlePinSubmit() {
    const valid = await verifyAdminPin(pinInput);
    if (valid) {
      setIsAdmin(true);
      setShowPinModal(false);
      setPinError(false);
      setPinInput("");
    } else {
      setPinError(true);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      const pin = localStorage.getItem("chart_admin_pin") || "";
      await updateProfile(name, { ...form, pin });
      await loadProfile();
      setEditing(false);
    } catch {
      // error
    } finally {
      setSaving(false);
    }
  }

  function updateBiz(index, field, value) {
    setForm(f => {
      const updated = [...(f.businesses || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...f, businesses: updated };
    });
  }

  async function handleDeleteCard(index) {
    if (!window.confirm("Delete this card image?")) return;
    try {
      const pin = localStorage.getItem("chart_admin_pin") || "";
      await deleteBusinessCard(name, index, pin);
      await loadProfile();
    } catch {
      // error
    }
  }

  async function handleMoveCard(fromIndex, toIndex) {
    try {
      const pin = localStorage.getItem("chart_admin_pin") || "";
      await reorderCards(name, fromIndex, toIndex, pin);
      await loadProfile();
    } catch {
      // error
    }
  }

  function sessionPin() {
    return isAdmin ? localStorage.getItem("chart_admin_pin") || "" : "";
  }

  async function handlePinAndUnlock() {
    const valid = await verifyAdminPin(pinInput);
    if (valid) {
      setIsAdmin(true);
      localStorage.setItem("chart_admin_pin", pinInput);
      setShowPinModal(false);
      setPinError(false);
    } else {
      setPinError(true);
    }
  }

  async function handleCardUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCard(true);
    try {
      const pin = localStorage.getItem("chart_admin_pin") || "";
      await uploadBusinessCard(name, file, pin);
      await loadProfile();
    } catch {
      // error
    } finally {
      setUploadingCard(false);
    }
  }

  // Check if admin PIN is cached
  useEffect(() => {
    const cached = localStorage.getItem("chart_admin_pin");
    if (cached) {
      verifyAdminPin(cached).then(valid => {
        if (valid) setIsAdmin(true);
        else localStorage.removeItem("chart_admin_pin");
      });
    }
  }, []);

  const colors = [
    "bg-orange-600", "bg-blue-600", "bg-purple-600", "bg-green-600",
    "bg-red-600", "bg-teal-600", "bg-indigo-600", "bg-pink-600", "bg-amber-600"
  ];
  const colorIdx = profile?.name ? profile.name.charCodeAt(0) % colors.length : 0;
  const profileUrl = `${window.location.origin}/member/${encodeURIComponent(name)}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center gap-4">
        <p className="text-zinc-500">Member not found</p>
        <Link to="/" className="text-[#1E7BC4] font-semibold text-sm hover:underline">← Back to members</Link>
      </div>
    );
  }

  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const hasInfo = profile.phone || profile.email || profile.businessName || profile.title || profile.website;
  const cards = profile.cardImages || (profile.cardImage ? [profile.cardImage] : []);
  const businesses = profile.businesses || [];

  return (
    <div className="min-h-screen bg-[#F5F2EC]">
      {/* Header */}
      <div className="bg-white/90 backdrop-blur-md border-b border-[#E5E0D8] sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={goBack} data-testid="back-to-tracker" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#5A7080] hover:text-[#0B2A47]">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {!isAdmin ? (
            <button
              data-testid="admin-unlock-btn"
              onClick={() => setShowPinModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-zinc-600 bg-zinc-100 hover:bg-zinc-200 rounded-md"
            >
              <Lock className="w-3.5 h-3.5" /> Admin
            </button>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-green-700 bg-green-50 rounded-md border border-green-200">
              <Lock className="w-3.5 h-3.5" /> Admin Mode
            </span>
          )}
        </div>
      </div>

      {/* PIN Modal */}
      {showPinModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div data-testid="pin-modal" className="bg-white rounded-lg p-6 w-full max-w-xs shadow-xl">
            <h3 className="text-sm font-bold text-zinc-950 mb-1">Admin Access</h3>
            <p className="text-xs text-zinc-500 mb-4">Enter admin PIN to edit profiles and roster.</p>
            <input
              data-testid="pin-input"
              type="password"
              inputMode="numeric"
              value={pinInput}
              onChange={e => { setPinInput(e.target.value); setPinError(false); }}
              onKeyDown={e => e.key === "Enter" && handlePinAndUnlock()}
              placeholder="PIN"
              className={`w-full px-3 py-2.5 text-center text-lg font-bold tracking-[0.3em] border-2 rounded-md outline-none ${
                pinError ? "border-red-400 bg-red-50" : "border-zinc-200 focus:border-orange-500"
              }`}
              autoFocus
            />
            {pinError && <p className="text-xs text-red-600 mt-1.5 text-center">Incorrect PIN</p>}
            <div className="flex gap-2 mt-4">
              <button onClick={() => { setShowPinModal(false); setPinInput(""); setPinError(false); }} className="flex-1 px-3 py-2 text-xs font-bold text-zinc-600 border border-zinc-200 rounded-md">Cancel</button>
              <button data-testid="pin-submit" onClick={handlePinAndUnlock} className="flex-1 px-3 py-2 text-xs font-bold text-white bg-orange-600 hover:bg-orange-700 rounded-md">Unlock</button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Profile Card */}
        <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden">
          {/* Top section */}
          <div className="p-6 flex items-center gap-4">
            {profile.headshot ? (
              <img src={profile.headshot} alt={profile.name} className="w-16 h-16 rounded-full object-cover flex-shrink-0 border-2 border-zinc-200" />
            ) : (
              <div className={`w-16 h-16 rounded-full ${colors[colorIdx]} text-white font-extrabold text-2xl flex items-center justify-center flex-shrink-0`}>
                {profile.name[0]}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h2 data-testid="profile-name" className="font-heading text-xl font-extrabold text-zinc-950 tracking-tight">{profile.name}</h2>
              {profile.title && <p className="text-sm text-zinc-500">{profile.title}</p>}
              {profile.businessName && <p className="text-sm font-semibold text-orange-600">{profile.businessName}</p>}
            </div>
            {isAdmin && !editing && (
              <button
                data-testid="edit-profile-btn"
                onClick={() => setEditing(true)}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-orange-600 border border-orange-200 bg-orange-50 hover:bg-orange-100 rounded-md"
              >
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
            )}
          </div>

          {/* Edit form */}
          {editing && isAdmin && (
            <div className="px-6 pb-6 border-t border-zinc-100 pt-4">
              <h4 className="text-[10px] font-bold text-zinc-400 tracking-[0.2em] uppercase mb-3">General</h4>
              <EditField label="Display Title" value={form.title} onChange={v => setForm(f => ({ ...f, title: v }))} placeholder="e.g. Owner, Sales Rep..." />
              <EditField label="Display Business Name" value={form.businessName} onChange={v => setForm(f => ({ ...f, businessName: v }))} placeholder="Company name..." />

              <h4 className="text-[10px] font-bold text-zinc-400 tracking-[0.2em] uppercase mt-5 mb-3">Businesses</h4>
              {(form.businesses || []).map((biz, i) => (
                <div key={i} className="border border-zinc-200 rounded-lg p-4 mb-3 bg-zinc-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-zinc-500">Business {i + 1}</span>
                    <button
                      onClick={() => {
                        const updated = [...(form.businesses || [])];
                        updated.splice(i, 1);
                        setForm(f => ({ ...f, businesses: updated }));
                      }}
                      className="text-[11px] font-bold text-red-500 hover:text-red-700 px-2 py-0.5 rounded hover:bg-red-50"
                    >
                      Remove
                    </button>
                  </div>
                  <EditField label="Business Name" value={biz.name} onChange={v => updateBiz(i, "name", v)} placeholder="Company name..." />
                  <EditField label="Title / Role" value={biz.title} onChange={v => updateBiz(i, "title", v)} placeholder="Your role..." />
                  <EditField label="Description" value={biz.description} onChange={v => updateBiz(i, "description", v)} placeholder="What does this business do?" />
                  <EditField label="Phone" value={biz.phone} onChange={v => updateBiz(i, "phone", v)} placeholder="555-000-0000" type="tel" />
                  <EditField label="Email" value={biz.email} onChange={v => updateBiz(i, "email", v)} placeholder="email@example.com" type="email" />
                  <EditField label="Website" value={biz.website} onChange={v => updateBiz(i, "website", v)} placeholder="www.example.com" />
                </div>
              ))}
              <button
                onClick={() => setForm(f => ({ ...f, businesses: [...(f.businesses || []), { name: "", title: "", phone: "", email: "", website: "", description: "" }] }))}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-blue-600 border border-blue-200 bg-blue-50 hover:bg-blue-100 rounded-md mb-4"
              >
                + Add Business
              </button>

              <div className="flex gap-2 mt-2">
                <button onClick={() => setEditing(false)} className="px-4 py-2 text-xs font-bold text-zinc-600 border border-zinc-200 rounded-md">Cancel</button>
                <button
                  data-testid="save-profile-btn"
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 text-xs font-bold text-white bg-orange-600 hover:bg-orange-700 rounded-md disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save Profile"}
                </button>
              </div>
            </div>
          )}

          {/* Contact info (view mode) */}
          {!editing && (
            <div className="px-6 pb-4">
              {businesses.length > 0 ? (
                <div className="space-y-5">
                  {businesses.map((biz, i) => (
                    <div key={i} className={`${i > 0 ? 'pt-4 border-t border-zinc-100' : ''}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Building2 className="w-4 h-4 text-orange-600" />
                        <p className="text-sm font-bold text-orange-600">{biz.name}</p>
                      </div>
                      {biz.title && (
                        <div className="flex items-start gap-3 py-1.5">
                          <Briefcase className="w-4 h-4 text-zinc-400 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-zinc-700">{biz.title}</p>
                        </div>
                      )}
                      {biz.description && (
                        <p className="text-xs text-zinc-500 ml-7 mb-1">{biz.description}</p>
                      )}
                      {biz.phone && (
                        <div className="flex items-start gap-3 py-1.5">
                          <Phone className="w-4 h-4 text-zinc-400 mt-0.5 flex-shrink-0" />
                          <a href={`tel:${biz.phone}`} className="text-sm text-zinc-700 hover:text-orange-600">{biz.phone}</a>
                        </div>
                      )}
                      {biz.email && (
                        <div className="flex items-start gap-3 py-1.5">
                          <Mail className="w-4 h-4 text-zinc-400 mt-0.5 flex-shrink-0" />
                          <a href={`mailto:${biz.email}`} className="text-sm text-zinc-700 hover:text-orange-600">{biz.email}</a>
                        </div>
                      )}
                      {biz.website && (
                        <div className="flex items-start gap-3 py-1.5">
                          <Globe className="w-4 h-4 text-zinc-400 mt-0.5 flex-shrink-0" />
                          <a href={biz.website.startsWith("http") ? biz.website : `https://${biz.website}`} target="_blank" rel="noopener noreferrer" className="text-sm text-zinc-700 hover:text-orange-600">{biz.website}</a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : hasInfo ? (
                <div>
                  <Field icon={Briefcase} label="Title" value={profile.title} />
                  <Field icon={Building2} label="Business" value={profile.businessName} />
                  <Field icon={Phone} label="Phone" value={profile.phone} />
                  <Field icon={Mail} label="Email" value={profile.email} />
                  <Field icon={Globe} label="Website" value={profile.website} />
                </div>
              ) : (
                <p className="text-sm text-zinc-400 py-4 text-center">
                  {isAdmin ? "No profile info yet. Click Edit to add details." : "No profile info added yet."}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Business Cards */}
        <div className="bg-white rounded-lg border border-zinc-200 mt-4 p-6">
          <h3 className="text-[10px] font-bold text-zinc-400 tracking-[0.2em] uppercase mb-4">Business Cards</h3>
          {cards.length > 0 ? (
            <div className="space-y-4">
              {cards.map((img, i) => (
                <div key={i}>
                  <img
                    data-testid={`business-card-image-${i}`}
                    src={img}
                    alt={`${profile.name}'s business card ${i + 1}`}
                    className="w-full rounded-lg rounded-b-none border border-zinc-200 shadow-sm"
                    style={isAdmin ? { borderBottom: "none" } : {}}
                  />
                  {isAdmin && (
                    <div className="flex items-center justify-end px-3 py-2.5 bg-zinc-100 border border-zinc-200 border-t-0 rounded-b-lg">
                      <div className="flex items-center gap-2">
                        {i > 0 && (
                          <button
                            onClick={() => handleMoveCard(i, i - 1)}
                            className="inline-flex items-center justify-center gap-1 min-w-[56px] px-3 py-2.5 text-xs font-bold text-zinc-700 bg-white border border-zinc-300 rounded-lg active:bg-zinc-200"
                          >
                            <ChevronUp className="w-4 h-4" /> Up
                          </button>
                        )}
                        {i < cards.length - 1 && (
                          <button
                            onClick={() => handleMoveCard(i, i + 1)}
                            className="inline-flex items-center justify-center gap-1 min-w-[56px] px-3 py-2.5 text-xs font-bold text-zinc-700 bg-white border border-zinc-300 rounded-lg active:bg-zinc-200"
                          >
                            <ChevronDown className="w-4 h-4" /> Down
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteCard(i)}
                          className="inline-flex items-center justify-center px-3 py-2.5 text-xs font-bold text-red-600 bg-white border border-red-300 rounded-lg active:bg-red-50"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {isAdmin && (
                <button
                  onClick={() => fileRef.current?.click()}
                  disabled={uploadingCard}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-orange-600 border border-orange-200 bg-orange-50 hover:bg-orange-100 rounded-md disabled:opacity-50"
                >
                  <Upload className="w-3.5 h-3.5" /> {uploadingCard ? "Uploading..." : "Add Another Card"}
                </button>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              {isAdmin ? (
                <>
                  <Camera className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                  <p className="text-sm text-zinc-400 mb-3">No business card uploaded yet</p>
                  <button
                    data-testid="upload-card-btn"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploadingCard}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-orange-600 hover:bg-orange-700 rounded-md disabled:opacity-50"
                  >
                    <Upload className="w-3.5 h-3.5" /> {uploadingCard ? "Uploading..." : "Upload Card Photo"}
                  </button>
                </>
              ) : (
                <>
                  <Camera className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                  <p className="text-sm text-zinc-400">No business card uploaded yet</p>
                </>
              )}
            </div>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleCardUpload}
          />
        </div>

        {/* QR Code & Share */}
        <div className="bg-white rounded-lg border border-zinc-200 mt-4 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Share2 className="w-4 h-4 text-zinc-400" />
            <h3 className="text-[10px] font-bold text-zinc-400 tracking-[0.2em] uppercase">Share Profile</h3>
          </div>

          {/* Short URL */}
          <div className="flex items-center gap-2 mb-5 bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2.5">
            <Globe className="w-4 h-4 text-zinc-400 flex-shrink-0" />
            <span className="text-sm font-mono text-zinc-700 truncate flex-1">{profileUrl}</span>
            <CopyButton text={profileUrl} />
          </div>

          {/* QR Code */}
          <div className="flex flex-col items-center gap-3">
            <div id="qr-container" className="bg-white p-4 rounded-lg border border-zinc-100 inline-block">
              <QRCodeSVG
                value={profileUrl}
                size={180}
                level="M"
                bgColor="#ffffff"
                fgColor="#18181b"
              />
              <p className="text-[10px] text-zinc-400 text-center mt-2 font-semibold">{profile.name} | CHART Networking</p>
            </div>
            <div className="flex gap-2">
              <button
                data-testid="save-qr-btn"
                onClick={() => saveQR(profile.name)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-zinc-600 border border-zinc-200 bg-white hover:bg-zinc-50 rounded-md"
              >
                <QrCode className="w-3.5 h-3.5" /> Save QR
              </button>
              <button
                data-testid="share-profile-btn"
                onClick={() => shareProfile(profile.name, profileUrl)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-orange-600 hover:bg-orange-700 rounded-md"
              >
                <Share2 className="w-3.5 h-3.5" /> Share
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
